import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doGetResumes, doCreateResume, doDeleteResume } from '../../../store/resume.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';


import Loader from '../../Loader';
import Button from '../../Button';
import { successVariant, infoVariant, warningVariant, errorVariant } from '../../../lib/barVariant';
import { IStaff } from '../../../models/employees.model';
import AddResumeModal from '../../modals/AddResumeModal';
import ConfirmModal from '../../modals/ConfirmModal';
import useAuth from '../../../hooks/useAuth';
import i18n from '../../../lib/i18n';

interface IProps {
  staff: IStaff | undefined;
  memberCode: string;
}

const StaffResume: FC<IProps> = ({ staff, memberCode }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();
  const { resumes, isLoading } = useSelector((state: RootState) => state.resume);

  const [resumeCode, setResumeCode] = useState('');
  const [addResumeVisible, setAddResumeVisible] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [deleteResumeVisible, setDeleteResumeVisible] = useState(false);

  useEffect(() => {
    getResumes();
  }, []);

  const getResumes = async () => {
    const res = await dispatch(doGetResumes(memberCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onSubmitResume = async () => {
    if (!staff) return;
    if (!resumeFile) {
      return enqueueSnackbar(t('campus.staff.message3'), infoVariant);
    }

    const form = new FormData();
    form.append('resume', resumeFile);

    const res = await dispatch(doCreateResume(staff.memberCode, form));

    switch (res?.status) {
      case 200:
        setAddResumeVisible(false);
        dispatch(doGetResumes(staff.memberCode));
        enqueueSnackbar(t('common.message.create'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onDeleteResume = async () => {
    if (!staff || !resumeCode) return;

    const res = await dispatch(doDeleteResume(resumeCode));

    switch (res?.status) {
      case 200:
        setDeleteResumeVisible(false);
        dispatch(doGetResumes(staff.memberCode));
        enqueueSnackbar(t('common.message.delete'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('campus.staff.column3'),
      key: 'filename',
      render: (value, record, index) => (
        <div className='text-left'>
          <a className='btn-text' href={resumes[index].fileLocation} download={resumes[index].originalName}>
            {resumes[index].originalName}
          </a>
        </div>
      ),
    },
    {
      title: t('campus.staff.column4'),
      key: 'regDate',
      render: (value, record, index) => moment(resumes[index].regDate).format('YYYY.MM.DD'),
    },
    {
      title: t('campus.staff.column5'),
      key: 'regMemKorName',
      render: (value, record, index) => (i18n.language === 'ko' ? resumes[index].regMemKorName : resumes[index].regMemEngName),
    },
    {
      title: '',
      key: 'button',
      render: (value, record, index) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className='btn-basic small gray'
            onClick={() => {
              setResumeCode(resumes[index].resumeCode);
              setDeleteResumeVisible(true);
            }}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      {isLoading ? <Loader /> : null}
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={resumes}
      />
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className='btn-basic pink-line'
            onClick={() => setAddResumeVisible(true)}
            text={t('campus.staff.button1')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {/* 이력서 추가 모달 */}
      <AddResumeModal
        addResumeVisible={addResumeVisible}
        setAddResumeVisible={setAddResumeVisible}
        onSubmitResume={onSubmitResume}
        resumeFile={resumeFile}
        setResumeFile={setResumeFile}
      />

      {/* 이력서 삭제 모달 */}
      <ConfirmModal
        visible={deleteResumeVisible}
        setVisible={setDeleteResumeVisible}
        onConfirm={onDeleteResume}
        titleText={t('campus.staff.title2')}
        messageText={t('campus.staff.text1')}
        confirmText={t('common.button.delete')}
        cancelText={t('common.button.cancel')}
      />
    </div>
  );
}

export default StaffResume;
